import { CellToggleComponent } from '@ag-grid/cell-templates/cell-toggle/cell-toggle.component';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { ConstructionProjectFeaturesType, PerspectiveDataType } from '@construction/models/construction-project.model';
import { ICellRendererAngularComp, AgGridAngular } from 'ag-grid-angular';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { EnabledFeature, PerspectiveChangeParam } from '../construction-features-list.component';
import { ICellRendererParams } from 'ag-grid-community';

@Component({
  selector: 'perspective-selection',
  templateUrl: './perspective-selection.component.html',
  styleUrls: ['./perspective-selection.component.scss'],
  imports: [AgGridAngular],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerspectiveSelectionComponent implements ICellRendererAngularComp {
  public nameMap: Record<string, string> = {
    aerial_data: 'Aerial Data',
    ground_data: 'Ground Data',
  };

  public defaultColDef: ColDef = {
    flex: 1,
    minWidth: 90,
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
  };

  public columnDefs: ColDef[] = [
    {
      field: '',
      minWidth: 50,
      sortable: false,
    },
    {
      field: 'name',
      headerName: 'Feature',
      minWidth: 200,
      sortable: false,
      valueGetter: (params: ValueGetterParams<PerspectiveDataType>) => {
        if (!params.data) return '';

        return this.nameMap[params.data.name];
      },
    },
    {
      field: 'owner_enabled',
      headerName: 'Owner',
      maxWidth: 100,
      sortable: false,
      cellRendererSelector: (params) => {
        return {
          component: CellToggleComponent,
          params: {
            isChecked: params.value,
            change: (checked: boolean) => {
              const perspective = params.data;
              perspective.owner_enabled = checked;
              this.switchToggled(this.feature, EnabledFeature.OwnerEnabled);
            },
          },
        };
      },
    },
    {
      field: 'epc_enabled',
      headerName: 'EPC',
      maxWidth: 100,
      sortable: false,
      cellRendererSelector: (params) => {
        return {
          component: CellToggleComponent,
          params: {
            isChecked: params.value,
            change: (checked: boolean) => {
              const perspective = params.data;
              perspective.epc_enabled = checked;
              this.switchToggled(this.feature, EnabledFeature.EpcEnabled);
            },
          },
        };
      },
    },
    {
      field: 'supplier_enabled',
      headerName: 'Supplier',
      // Hidden while Logistics & Inventory (the only supplier-enabled feature) is hidden.
      hide: true,
      maxWidth: 100,
      sortable: false,
      cellRendererSelector: (params) => {
        return {
          component: CellToggleComponent,
          params: {
            isChecked: params.value,
            isDisabled: true,
            change: (checked: boolean) => {
              const perspective = params.data;
              perspective.supplier_enabled = checked;
              this.switchToggled(this.feature, EnabledFeature.SupplierEnabled);
            },
          },
        };
      },
    },
  ];

  public readonly rowData = signal<PerspectiveDataType[]>([]);

  private params: ICellRendererParams<ConstructionProjectFeaturesType> & PerspectiveChangeParam;
  private feature: ConstructionProjectFeaturesType;

  public agInit(params: ICellRendererParams<ConstructionProjectFeaturesType> & PerspectiveChangeParam): void {
    this.params = params;

    if (params.data) {
      this.feature = params.data;
      this.rowData.set([params.data.aerial_data!, params.data.ground_data!]);
    }
  }

  public refresh(): boolean {
    return false;
  }

  private switchToggled(feature: ConstructionProjectFeaturesType, role: EnabledFeature): void {
    this.params.perspectiveChange(feature, role);
  }
}
